/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, ComponentFactoryResolver, ComponentRef, HostListener, Input, ViewChild, ViewContainerRef, Output} from '@angular/core';
import {Client, ProfileInfoType, ReportClientUser, ReportUser, Helpers, ReportClientType, User} from '../../../../../model';
import {ClientUserInfoContentComponent} from './client-user-info-content/client-user-info-content.component';
import {ClientInfoContentComponent} from './client-info-content/client-info-content.component';
import {CreateNewClientModal, CreateNewClientUserModal, CreateNewUserModal} from '../../../../modal';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs/internal/Subject';
import { UserInfoContentComponent } from './user-info-content/user-info-content.component';
import { EventEmitter } from '@angular/core';

@Component({
  selector: 'profile-info',
  templateUrl: 'profile-info.component.html',
  styleUrls: ['profile-info.component.scss']
})
export class ProfileInfoComponent {
  @ViewChild('target', { read: ViewContainerRef }) target: ViewContainerRef;
  @Input() data: any;
  @Input() disabled = false;
  @Input() showHover = true;
  @Input() profileInfoType: ProfileInfoType;
  @Output() updatedValueEmit = new EventEmitter();

  private tooltip: ComponentRef<any>;
  private visible: boolean;

  editModal: BsModalRef;

  constructor(
    private viewContainerRef: ViewContainerRef,
    private resolver: ComponentFactoryResolver,
    private modalService: BsModalService
  ) {}

  onClick(): void {
    if (this.data) {
      switch (this.profileInfoType) {
      case ProfileInfoType.CLIENT:
        this.editModal = this.modalService.show(CreateNewClientModal, Helpers.getModalConfig());
        // eslint-disable-next-line no-case-declarations
        const clAckSubject = new Subject<boolean>();
        this.editModal.content.subject = clAckSubject;
        this.editModal.content.client = <Client>this.data;
        this.editModal.content.isEdit = true;

        clAckSubject.subscribe((value: any) => {
          if (value) {
            this.updatedValueEmit.emit(value);
          }
        });
        break;
      case ProfileInfoType.CLIENT_USER:
        if ((<ReportClientUser>this.data).CLIENT_USER) {
          this.editModal = this.modalService.show(CreateNewClientUserModal, Helpers.getModalConfig());
          const clUsrAckSubject = new Subject<boolean>();
          this.editModal.content.subject = clUsrAckSubject;
          this.editModal.content.clientUser = (<ReportClientUser>this.data).CLIENT_USER;
          this.editModal.content.manageFlag = true;
          this.editModal.content.isEdit = true;
          this.editModal.content.isLender = (<ReportClientUser>this.data).CLIENT_USER.CLIENT && (<ReportClientUser>this.data).TYPE !== ReportClientType.OTHER && (<ReportClientUser>this.data).CLIENT_USER.CLIENT.COLLECTOR !== true;
          clUsrAckSubject.subscribe(value => {
          });
        }
        break;
      case ProfileInfoType.USER:
        if ((<ReportUser>this.data).USER) {
          this.editModal = this.modalService.show(CreateNewUserModal, Helpers.getModalConfig());
          const clUsrAckSubject = new Subject<boolean>();
          this.editModal.content.subject = clUsrAckSubject;
          this.editModal.content.user = (<User>this.data.USER);
          this.editModal.content.isEdit = true;
          clUsrAckSubject.subscribe(value => {
          });
        }
        break;
      default:
        return;
      }
    }
  }

  @HostListener('focusin')
  @HostListener('mouseenter')
  showInfo(): void {
    if (this.visible || this.disabled || !this.showHover)
      return;

    this.visible = true;
    let factory;

    switch (this.profileInfoType) {
    case ProfileInfoType.CLIENT:
      factory = this.resolver.resolveComponentFactory(ClientInfoContentComponent);
      break;
    case ProfileInfoType.CLIENT_USER:
      factory = this.resolver.resolveComponentFactory(ClientUserInfoContentComponent);
      break;
    case ProfileInfoType.USER:
      factory = this.resolver.resolveComponentFactory(UserInfoContentComponent);
      break;
    default:
      return;
    }

    this.tooltip = this.target.createComponent(factory);
    this.tooltip.instance.hostElement = this.viewContainerRef.element.nativeElement;

    switch (this.profileInfoType) {
    case ProfileInfoType.CLIENT:
      this.tooltip.instance.data = this.data;
      break;
    case ProfileInfoType.CLIENT_USER:
      this.tooltip.instance.data = this.data;
      break;
    case ProfileInfoType.USER:
      this.tooltip.instance.data = this.data;
      break;
    default:
      return;
    }
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  hideInfo(): void {
    if (!this.visible)
      return;

    this.visible = false;
    if (this.tooltip)
      this.tooltip.destroy();
  }
}


